import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageTitle, statusColor } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ComplianceNotice } from "@/components/Disclaimers";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";

const SERVICES = ["Assisted Living", "Family Care Homes", "Memory Care", "Independent Living",
  "Private-Duty Home Care", "Personal Care", "Companion Care", "Residential Respite", "In-Home Respite",
  "Adult Day Services", "Care Management", "Senior Transition / Move Management", "Private Transportation",
  "Private-Pay Behavioral / Community Support Services"];
const PAYERS = ["Private Pay", "Long-Term Care Insurance", "Veterans Aid & Attendance", "Medicare", "Medicaid",
  "1915(i)", "Innovations Waiver", "TRICARE", "CHAMPVA", "CHIP", "Other Government Program"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/agent/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const empty = { lead_nickname: "", geographic_area: "", state: "", service_category: "Assisted Living",
    budget_range: "", private_pay_confirmed: false, payer_type: "Private Pay", urgency: "Standard",
    preferred_contact_method: "", contact_permission: false, phone: "", email: "", source: "Direct", notes: "" };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/agent/leads", f);
      if (data.compliance_flag) toast.warning("Lead created — COMPLIANCE REVIEW REQUIRED (government payer/sensitive service).");
      else if (data.duplicate) toast.warning("Lead created — possible duplicate flagged for admin review.");
      else toast.success(`Lead ${data.lead_id} created`);
      setOpen(false); setF(empty); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="My Leads" subtitle="Submit and track private-pay opportunities">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-lead-btn" className="bg-navy hover:bg-navy/90"><Plus className="mr-2 h-4 w-4" /> New Lead</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Submit a new lead</DialogTitle></DialogHeader>
            <ComplianceNotice className="my-2" />
            <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <FF label="Lead nickname / initials" req><Input data-testid="lead-nickname" required value={f.lead_nickname} onChange={set("lead_nickname")} /></FF>
                <FF label="Service category" req>
                  <Select value={f.service_category} onValueChange={(v) => setF({ ...f, service_category: v })}>
                    <SelectTrigger data-testid="lead-service"><SelectValue /></SelectTrigger>
                    <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </FF>
                <FF label="Geographic area" req><Input data-testid="lead-area" required value={f.geographic_area} onChange={set("geographic_area")} /></FF>
                <FF label="State" req><Input data-testid="lead-state" required value={f.state} onChange={set("state")} /></FF>
                <FF label="Budget range"><Input data-testid="lead-budget" value={f.budget_range} onChange={set("budget_range")} placeholder="e.g. $4k–6k/mo" /></FF>
                <FF label="Payer type">
                  <Select value={f.payer_type} onValueChange={(v) => setF({ ...f, payer_type: v })}>
                    <SelectTrigger data-testid="lead-payer"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </FF>
                <FF label="Urgency">
                  <Select value={f.urgency} onValueChange={(v) => setF({ ...f, urgency: v })}>
                    <SelectTrigger data-testid="lead-urgency"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Immediate", "Soon", "Standard", "Planning"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </FF>
                <FF label="Preferred contact method"><Input data-testid="lead-contact-method" value={f.preferred_contact_method} onChange={set("preferred_contact_method")} /></FF>
                <FF label="Phone (non-PHI contact)"><Input data-testid="lead-phone" value={f.phone} onChange={set("phone")} /></FF>
                <FF label="Email"><Input data-testid="lead-email" value={f.email} onChange={set("email")} /></FF>
              </div>
              <FF label="Non-clinical notes"><Textarea data-testid="lead-notes" value={f.notes} onChange={set("notes")} placeholder="Business-development notes only. No medical records." /></FF>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Checkbox data-testid="lead-privatepay" checked={f.private_pay_confirmed} onCheckedChange={(v) => setF({ ...f, private_pay_confirmed: !!v })} /> Private-pay confirmed
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Checkbox data-testid="lead-permission" checked={f.contact_permission} onCheckedChange={(v) => setF({ ...f, contact_permission: !!v })} /> Contact permission obtained
                </label>
              </div>
              <Button type="submit" data-testid="lead-submit-btn" className="w-full bg-navy hover:bg-navy/90">Submit lead</Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageTitle>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>{["Lead ID", "Nickname", "Service", "Area", "Payer", "Status", "Flags"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{l.lead_id}</td>
                  <td className="px-4 py-3 font-medium text-navy">{l.lead_nickname}</td>
                  <td className="px-4 py-3 text-slate-600">{l.service_category}</td>
                  <td className="px-4 py-3 text-slate-600">{l.geographic_area}, {l.state}</td>
                  <td className="px-4 py-3 text-slate-600">{l.payer_type}</td>
                  <td className="px-4 py-3"><Badge className={`${statusColor(l.status)} hover:${statusColor(l.status)}`}>{(l.status || "").replace(/_/g, " ")}</Badge></td>
                  <td className="px-4 py-3">
                    {l.compliance_flag && <AlertTriangle className="inline h-4 w-4 text-amberbrand" title="Compliance" />}
                    {l.duplicate_flag && <span className="ml-1 text-xs font-semibold text-amberbrand">DUP</span>}
                  </td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No leads yet. Click "New Lead" to begin.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function FF({ label, req, children }) {
  return <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}{req && " *"}</Label><div className="mt-1">{children}</div></div>;
}
